/**
 * UpcomingFollowupsWidget (R4 — dashboard)
 * Shows next 5 pending collection follow-ups, sorted by due_date.
 * Overdue items highlighted in red. Click-through to invoice.
 * Empty state: "No upcoming collection follow-ups."
 */
import { useUpcomingFollowups, type UpcomingFollowupItem } from '@/hooks/useCollectionFollowups';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

function formatDate(isoDate: string): string {
  return new Date(isoDate + 'T00:00:00').toLocaleDateString('en-CA', {
    month: 'short',
    day: 'numeric',
  });
}

function formatCents(cents: number): string {
  return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(
    cents / 100
  );
}

function FollowupRow({ item }: { item: UpcomingFollowupItem }) {
  const navigate = useNavigate();

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate(`/invoices/${item.invoice_id}`)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') navigate(`/invoices/${item.invoice_id}`);
      }}
      className="flex items-start gap-2 p-2 rounded text-sm cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors"
      data-testid={`followup-item-${item.id}`}
    >
      <Bell
        size={14}
        className={`mt-0.5 flex-shrink-0 ${item.is_overdue ? 'text-red-500' : 'text-blue-500'}`}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <span
            className={`font-medium truncate ${item.is_overdue ? 'text-red-600' : 'text-gray-900'}`}
          >
            {formatDate(item.due_date)}
            {item.is_overdue && (
              <Badge variant="destructive" className="ml-1.5 text-xs px-1 py-0 align-middle">
                Overdue
              </Badge>
            )}
          </span>
          {item.expected_amount_cents !== null && (
            <span className="text-xs font-medium text-gray-900 flex-shrink-0">
              {formatCents(item.expected_amount_cents)}
            </span>
          )}
        </div>
        <div className="text-xs text-gray-600 truncate">
          {item.invoice_number}
          {item.client_name ? ` · ${item.client_name}` : ''}
          {item.note ? ` · ${item.note}` : ''}
        </div>
      </div>
    </div>
  );
}

export function UpcomingFollowupsWidget() {
  const { data, isLoading, error } = useUpcomingFollowups(5);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Bell className="h-5 w-5" />
            Collection Follow-ups
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 animate-pulse">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Bell className="h-5 w-5" />
            Collection Follow-ups
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Failed to load follow-ups</p>
        </CardContent>
      </Card>
    );
  }

  const overdueCount = data?.filter((i) => i.is_overdue).length ?? 0;

  return (
    <Card data-testid="upcoming-followups-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Bell className="h-5 w-5" />
            Collection Follow-ups
          </CardTitle>
          {overdueCount > 0 && (
            <Badge variant="destructive" className="text-xs">
              {overdueCount} overdue
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {!data || data.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="followups-empty-state">
            No upcoming collection follow-ups.
          </p>
        ) : (
          <div className="space-y-2">
            {data.map((item) => (
              <FollowupRow key={item.id} item={item} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
